import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import EmptyState from "@/components/base/EmptyState";
import MovieGrid from "@/components/feature/MovieGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import ShareModal from "@/components/feature/ShareModal";
import { formatViews, getMovieById, getRelatedMovies, getRelatedNews, typeLabel } from "@/utils/media";
import { useSeo } from "@/hooks/useSeo";

export default function DetailPage() {
  const { id = "" } = useParams();
  const movie = getMovieById(id);
  const [shareOpen, setShareOpen] = useState(false);

  const related = movie ? getRelatedMovies(movie, 10) : [];
  const relatedNews = movie ? getRelatedNews(movie, 4) : [];

  useSeo({
    title: movie ? `${movie.title} - 91影视详情` : "影视详情 - 91影视",
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}${typeLabel(movie.type)}）：${movie.summary.slice(0, 70)}`
      : "影视作品详情介绍。",
    keywords: movie
      ? `91影视,${movie.title},${movie.genres.join(",")},${movie.region}${typeLabel(movie.type)}`
      : "91影视,影视详情",
  });

  if (!movie) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-file-unknow-line"
          title="影片不存在或已下架"
          description="该影片可能已被移除，返回首页看看其他精彩内容吧。"
          actionLabel="返回首页"
          to="/"
        />
      </div>
    );
  }

  const infoRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join("、") },
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "地区", value: movie.region },
    { label: "年份", value: `${movie.year}` },
    { label: "片长", value: movie.duration },
  ];

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/category/${movie.type}`} className="transition-colors hover:text-primary-600">
          {typeLabel(movie.type)}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">{movie.title}</span>
      </nav>

      <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr] lg:gap-8">
        <div>
          <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={movie.poster}
              alt={`${movie.title} 海报`}
              title={`${movie.title} - 91影视`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="mt-4 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
          >
            <i className="ri-share-forward-line"></i>
            分享影片
          </button>
          <div className="mt-3 flex items-start gap-2 rounded-md bg-secondary-50 p-3 text-xs text-secondary-900">
            <i className="ri-information-line mt-0.5 text-base"></i>
            <span>本站暂未开放播放与下载功能，当前仅提供影片信息展示。</span>
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-extrabold text-foreground-950 md:text-3xl">{movie.title}</h1>
            <span className="flex items-center gap-1 rounded-md bg-secondary-500 px-2 py-0.5 text-sm font-bold text-foreground-950">
              <i className="ri-star-fill"></i>
              {movie.rating.toFixed(1)}
            </span>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">
              {typeLabel(movie.type)}
            </span>
            {movie.genres.map((genre) => (
              <span key={genre} className="rounded-md bg-background-200 px-2.5 py-1 text-xs text-foreground-600">
                {genre}
              </span>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {movie.year}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-earth-line"></i>
              {movie.region}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {movie.duration}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-accent-500"></i>
              {formatViews(movie.views)} 次浏览
            </span>
          </div>

          <section className="mt-6">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
              <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
              剧情简介
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{movie.summary}</p>
          </section>

          <section className="mt-6">
            <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950">
              <span className="h-5 w-1.5 rounded-full bg-accent-500"></span>
              基本信息
            </h2>
            <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 rounded-lg border border-background-200 bg-background-50 p-4 sm:grid-cols-2">
              {infoRows.map((row) => (
                <div key={row.label} className="flex gap-3 text-sm">
                  <dt className="w-14 shrink-0 text-foreground-500">{row.label}</dt>
                  <dd className="text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>
      </div>

      <section className="mt-10">
        <SectionHeading title="相关影视资讯" subtitle={`与《${movie.title}》相关的新闻与解读`} moreTo="/news" />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {relatedNews.map((item) => (
            <Link
              key={item.id}
              to={`/news/${item.id}`}
              className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                <i className="ri-newspaper-line text-lg"></i>
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-foreground-900 group-hover:text-primary-600">
                  {item.title}
                </p>
                <p className="mt-1 text-xs text-foreground-500">
                  {item.category} · {item.date}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <SectionHeading title="推荐影视作品" subtitle="根据类型与题材为你精选" moreTo={`/category/${movie.type}`} />
        <MovieGrid movies={related} />
      </section>

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={movie.title}
        url={typeof window !== "undefined" ? window.location.href : ""}
      />
    </div>
  );
}